import   React      ,
       { useState   ,
         useRef     ,
         useEffect  ,
         useCallback }  from 'react';

import   classNames     from 'classnames';

import   styles         from './SoundPlayer.module.scss';

import   Icon           from '../images/Icon';

/**
 * Un lecteur de son minimal, entièrement dessiné par nous.
 *
 * ── Pourquoi ne pas garder <audio controls> ──────────────────────────────────
 *
 * La prévisualisation du son d'attente d'une chaîne (et celle des fiches Son et
 * Diaporama sonore) affichait le lecteur natif du navigateur. Or ce lecteur n'est
 * pas un élément HTML ordinaire : c'est un shadow DOM que chaque moteur dessine à
 * sa façon, et que l'on ne peut styler qu'à travers des pseudo-éléments
 * PROPRIÉTAIRES (`::-webkit-media-controls-*`). Le fond sombre appliqué à
 * `.audio_element` tenait sur Chrome/Linux et se défaisait sur Mac : Safari ne
 * respecte pas les mêmes pseudo-éléments, et la barre revenait claire, aux
 * mauvaises dimensions, avec un texte devenu illisible.
 *
 * Il n'existe pas de correctif CSS portable à ce problème : la seule issue est de
 * ne plus afficher le lecteur natif du tout. L'élément `<audio>` reste — c'est lui
 * qui décode et qui joue —, mais sans `controls`, donc invisible ; tout ce qui se
 * voit ici est du DOM ordinaire, identique sur toutes les plateformes.
 *
 * ── Ce que le composant fait, et rien de plus ────────────────────────────────
 *
 * Lire/mettre en pause, la position et la durée, une ligne de vie cliquable, un
 * réglage de volume. Pas de vitesse de lecture, pas de boucle, pas de forme d'onde :
 * c'est une prévisualisation, pas un outil de montage.
 *
 * ⚠️ Les barres sont dessinées avec des <div> et non avec `<input type="range">`,
 * pour la raison qui a motivé tout le composant : un `range` est lui aussi rendu
 * par le moteur, et retrouver le même dessin sur Chrome et sur Safari demande une
 * pile de pseudo-éléments propriétaires. Un div, lui, s'affiche partout pareil.
 */
export interface SoundPlayerProps {
  /** L'URL du son : http(s), chemin absolu, ou blob: d'un fichier choisi localement. */
  src         : string;
  autoPlay?   : boolean;
  /** Volume de départ, de 0 à 1. */
  volume?     : number;
  /** Largeur du lecteur ; par défaut il occupe toute la place disponible. */
  width?      : string;
  className?  : string;
  /** Appelé quand la source est illisible — c'est à l'appelant d'afficher le message. */
  onError?    : () => void;
}

/**
 * `m:ss`, ou `h:mm:ss` au-delà de l'heure.
 *
 * Rend `--:--` tant que la durée n'est pas connue : avant que les métadonnées ne
 * soient chargées, `duration` vaut NaN, et pour un flux sans fin, Infinity.
 */
const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) { return '--:--'; }

  const total   = Math.floor(seconds);
  const hours   = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs    = total % 60;

  return hours > 0
    ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    : `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Une barre horizontale que l'on peut pointer et faire glisser.
 *
 * Sert deux fois : la ligne de vie et le réglage de volume. Le suivi du glissement
 * est posé sur `window` et non sur la barre : sans cela, sortir du rectangle en
 * cours de glissement fige la valeur là où le curseur est sorti.
 */
const DraggableBar = ({
  ratio,
  onChange,
  className,
  ariaLabel,
}: {
  ratio     : number;                    // 0 à 1
  onChange  : (ratio: number) => void;
  className?: string;
  ariaLabel : string;
}) => {

  const trackRef              = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  // `onChange` est recréé à chaque rendu du parent, et le parent se rend quatre fois
  // par seconde pendant la lecture (onTimeUpdate). Le garder dans une référence évite
  // de défaire et refaire les écouteurs de `window` à ce rythme pendant un glissement.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const ratioFromEvent = useCallback((clientX: number): number => {
    const track = trackRef.current;
    if (!track) { return 0; }

    const rect = track.getBoundingClientRect();
    if (rect.width === 0) { return 0; }

    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  }, []);

  useEffect(() => {
    if (!dragging) { return; }

    const handleMove = (event: MouseEvent) => onChangeRef.current(ratioFromEvent(event.clientX));
    const handleUp   = ()                  => setDragging(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup',   handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup',   handleUp);
    };
  }, [dragging, ratioFromEvent]);

  const percent = `${Math.min(100, Math.max(0, ratio * 100))}%`;

  return (
    <div ref             = {trackRef}
         className       = {classNames(styles.track, className)}
         role            = "slider"
         aria-label      = {ariaLabel}
         aria-valuemin   = {0}
         aria-valuemax   = {100}
         aria-valuenow   = {Math.round(ratio * 100)}
         tabIndex        = {0}
         onMouseDown     = {(event) => { setDragging(true); onChange(ratioFromEvent(event.clientX)); }}
         onKeyDown       = {(event) => {
           if (event.key === 'ArrowLeft')  { event.preventDefault(); onChange(Math.max(0, ratio - 0.05)); }
           if (event.key === 'ArrowRight') { event.preventDefault(); onChange(Math.min(1, ratio + 0.05)); }
         }} >
      <div className={classNames(styles.trackFill)} style={{ width: percent }} />
      <div className={classNames(styles.trackKnob)} style={{ left:  percent }} />
    </div>
  );
};

export const SoundPlayer = ({
  src,
  autoPlay  = false,
  volume    = 1,
  width     = '100%',
  className,
  onError,
}: SoundPlayerProps) => {

  const audioRef = useRef<HTMLAudioElement>(null);
  const rowRef   = useRef<HTMLDivElement>(null);

  const [playing,      setPlaying]      = useState(false);
  const [currentTime,  setCurrentTime]  = useState(0);
  const [duration,     setDuration]     = useState(NaN);
  const [level,        setLevel]        = useState(Math.min(1, Math.max(0, volume)));
  const [volumeOpen,   setVolumeOpen]   = useState(false);

  // Changer de source remet le lecteur à zéro : sans ça, la position et la durée de
  // l'ancien son restent affichées jusqu'au premier événement du nouveau.
  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(NaN);
  }, [src]);

  // Le volume vit sur l'élément, pas dans le rendu : on le pousse à chaque changement.
  useEffect(() => {
    if (audioRef.current) { audioRef.current.volume = level; }
  }, [level, src]);

  // Le réglage de volume est un panneau flottant : un clic ailleurs le referme.
  useEffect(() => {
    if (!volumeOpen) { return; }

    const handleOutside = (event: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(event.target as Node)) { setVolumeOpen(false); }
    };

    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [volumeOpen]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) { return; }

    if (audio.paused) {
      // La lecture peut être refusée (règles d'autoplay du navigateur) : la promesse
      // rejetée doit être attrapée, sinon elle remonte en erreur non gérée.
      void audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  };

  const seekToRatio = (ratio: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(duration) || duration <= 0) { return; }

    audio.currentTime = ratio * duration;
    setCurrentTime(audio.currentTime);
  };

  const progressRatio = Number.isFinite(duration) && duration > 0 ? currentTime / duration : 0;

  return (
    <div className={classNames(styles.player, className)} style={{ width }}>

      {/* Le lecteur natif n'est jamais affiché : il ne fait que décoder et jouer. */}
      <audio ref             = {audioRef}
             src             = {src}
             autoPlay        = {autoPlay}
             preload         = "metadata"
             onLoadedMetadata= {(event) => setDuration(event.currentTarget.duration)}
             onDurationChange= {(event) => setDuration(event.currentTarget.duration)}
             onTimeUpdate    = {(event) => setCurrentTime(event.currentTarget.currentTime)}
             onPlay          = {() => setPlaying(true)}
             onPause         = {() => setPlaying(false)}
             onEnded         = {() => { setPlaying(false); setCurrentTime(0); }}
             onError         = {onError} />

      <div className={classNames(styles.row)} ref={rowRef}>

        <button type      = "button"
                className = {classNames(styles.button)}
                onClick   = {togglePlay}
                title     = {playing ? 'Pause' : 'Lecture'}
                aria-label= {playing ? 'Pause' : 'Lecture'} >
          <Icon name       = {playing ? 'Pause' : 'Play'}
                color      = "var(--color-Field-Icon-normal)"
                hoverColor = "var(--color-Field-FG-normal)"
                width      = {16}
                height     = {16}
                clickable  = {false} />
        </button>

        <div className={classNames(styles.time)}>
          {`${formatTime(currentTime)} / ${formatTime(duration)}`}
        </div>

        <DraggableBar ratio     = {progressRatio}
                      onChange  = {seekToRatio}
                      ariaLabel = "Position dans le son" />

        <div className={classNames(styles.volume)}>
          <button type      = "button"
                  className = {classNames(styles.button)}
                  onClick   = {() => setVolumeOpen(open => !open)}
                  title     = "Volume"
                  aria-label= "Volume" >
            <Icon name       = {level === 0 ? 'VolumeX' : 'Volume2'}
                  color      = "var(--color-Field-Icon-normal)"
                  hoverColor = "var(--color-Field-FG-normal)"
                  width      = {16}
                  height     = {16}
                  clickable  = {false} />
          </button>

          {
           volumeOpen &&
           (
            <div className={classNames(styles.volumePopover)}>
              <DraggableBar ratio     = {level}
                            onChange  = {setLevel}
                            className = {classNames(styles.volumeTrack)}
                            ariaLabel = "Volume" />
            </div>
           )
          }
        </div>

      </div>
    </div>
  );
};

export default SoundPlayer;
